import type { Translation } from "../i18n-types.js"

const es = {
  common: {
    countViews: "{{0 vistas|1 vista|?? vistas}}",
    countReactions: "{{0 reacciones|1 reacción|?? reacciones}}",
    back: "Volver",
    blog: "Artículos",
    settings: "Configuración",
    addPosts: "Crear artículo",
    save: "Guardar",
    filter: "Filtro",
    tag: "Etiqueta",
    sort: "Ordenar",
    all: "Todos",
    loading: "Cargando...",
    saving: "Guardando...",
    delete: "Eliminar",
    edit: "Editar",
    close: "Cerrar",
    error: "Error al guardar datos",
    networkError: "Error al guardar datos. Por favor, comprueba tu conexión",
    validationError: "Por favor, revisa el formulario",
    by: "por",
    cancel: "Cancelar",
    private: "Privado",
    holdToConfirm: "Mantén para confirmar",
    done: "¡Hecho!",
    defaultLanguage: "por defecto",
    translating: "Traduciendo...",
    translateAll: "Traducir todo",
    translateFrom: "Traducir desde {language}",
  },
  mainLayout: {
    errorLoadingData: "Hubo un error al cargar los datos de esta página. Por favor, inténtalo de nuevo.",
    reloadPage: "Recargar página",
  },
  dashboard: {
    welcomeBack: "Bienvenido de nuevo",
    latestPosts: "Últimos artículos",
    viewAllPosts: "Ver todos",
    noPostsYet: "Aún no has publicado ningún artículo.",
    latestDrafts: "Últimos borradores",
    viewAllDrafts: "Ver todos",
    noDrafts: "No tienes ningún borrador",
    latestNotes: "Últimas notas",
    noNotesYet: "Aún no tienes ninguna nota",
    subscribers: "Suscriptores",
    posts: "Artículos",
    views: "Vistas",
    openRate: "Tasa de apertura",
    published: "Publicado",
    created: "Creado",
    recentReactions: "Reacciones recientes",
    noReactionsYet: "Aún no tienes reacciones",
    reactedToYourPost: "reaccionaron a tu publicación",
    othersCount: "{{ninguna otra|1 otra persona|?? otras personas}}",
    createPublication: {
      title: "Crea tu propia publicación",
      description: "Comienza tu propio rincón en la web. comparte tus pensamientos, construye tu audiencia y posee tu contenido.",
      button: "Comenzar",
    },
    verifyEmail: {
      title: "Verifica tu correo",
      description: "Verifica tu dirección de correo electrónico para desbloquear todas las funciones, como crear tu propia publicación.",
      button: "Verificar correo",
    },
  },
  settings: {
    title: "Configuración",
    localizationTitle: "Localización",
    localizationDescription: "Configura tu idioma preferido",
    interfaceLanguageLabel: "Idioma de la interfaz",
    authenticationTitle: "Autenticación",
    authenticationDescription: "Gestiona tu cuenta",
    signedInAs: "Sesión iniciada como {email}",
    signOut: "Cerrar sesión",
    signingOut: "Cerrando sesión...",
    authorProfileTitle: "Perfil de autor",
    authorProfileDescription: "Información sobre ti que se mostrará en tu publicación",
    profileImageLabel: "Foto de perfil (120×120)",
    authorNameLabel: "Nombre del autor",
    authorNamePlaceholder: "Tu nombre",
    bioLabel: "Bio",
    bioPlaceholder: "Cuéntale a tus lectores sobre ti",
    imageSaved: "Imagen subida",
    imageDeleted: "Imagen eliminada",
    informationSaved: "Guardado correctamente",
    social: {
      title: "redes sociales",
      description: "conecta tus perfiles sociales a tu publicación",
      social_x: {
        label: "X (Twitter)",
        placeholder: "https://x.com/nombredeusuario",
      },
      social_instagram: {
        label: "Instagram",
        placeholder: "https://instagram.com/nombredeusuario",
      },
      social_tiktok: {
        label: "TikTok",
        placeholder: "https://tiktok.com/@nombredeusuario",
      },
      social_youtube: {
        label: "YouTube",
        placeholder: "https://youtube.com/@nombredeusuario",
      },
      social_facebook: {
        label: "Facebook",
        placeholder: "https://facebook.com/nombredeusuario",
      },
      social_linkedin: {
        label: "LinkedIn",
        placeholder: "https://linkedin.com/in/nombredeusuario",
      },
      social_bluesky: {
        label: "Bluesky",
        placeholder: "https://bsky.app/profile/nombredeusuario",
      },
      social_books: {
        label: "Libros",
        placeholder: "https://goodreads.com/user/show/nombredeusuario",
      },
      social_website: {
        label: "Sitio web",
        placeholder: "https://tusitio.com",
      },
      social_soundcloud: {
        label: "SoundCloud",
        placeholder: "https://soundcloud.com/nombredeusuario",
      },
      social_bandcamp: {
        label: "Bandcamp",
        placeholder: "https://nombredeusuario.bandcamp.com",
      },
    },
    subscriptions: {
      empty: "Aún no tienes suscripciones",
      unsubscribeTitle: "Cancelar suscripción",
      unsubscribeDescription: "Ya no recibirás actualizaciones de esta publicación.",
      unsubscribePaidDisclaimer: "Tu plan de pago actual también se cancelará.",
      holdToUnsubscribe: "Mantén para cancelar",
      unsubscribing: "Cancelando...",
      unsubscribed: "Suscripción cancelada",
    },
    deleteAccount: {
      title: "Eliminar cuenta",
      description: "Elimina permanentemente tu cuenta y todos los datos",
      warning: "Esta acción no se puede deshacer. Todos los artículos, notas y datos de suscriptores se eliminarán permanentemente.",
      button: "Eliminar cuenta",
      confirmTitle: "¿Eliminar tu cuenta?",
      confirmDescription: "Esto eliminará permanentemente tu cuenta y todo el contenido. Esta acción no se puede deshacer.",
      exportBeforeDeletePrefix: "Antes de eliminar, puedes",
      exportBeforeDeleteSuffix: "los datos de tu publicación, incluyendo todos los artículos, notas e información de suscriptores.",
      typeToConfirm: "Escribe {text} para confirmar",
      confirmButton: "Eliminar mi cuenta",
    },
  },
  post: {
    preview: "Vista previa",
    previewDescription: "Ve cómo se vería tu publicación para tus lectores.",
    unarchive: "Desarchivar",
    update: "Actualizar",
    updated: "Publicación actualizada correctamente",
    publish: "Continuar",
    uploadHeaderImage: "Subir imágenes de cabecera (16:9, 800 × 450 px)",
    title: "Título",
    subtitle: "Subtítulo",
    statusSpam: "Esta versión está marcada como spam; contacta con soporte si crees que es un error.",
    statusArchived: "Esta versión está archivada; la gente no puede verla, pero aún puedes editarla.",
    statusSent: "Esta versión se ha enviado a tus suscriptores por correo",
    statusPublished: "Esta versión está publicada y visible para tus lectores",
    statusDraft: "Esta es una versión en borrador; puedes previsualizarla pero no es visible para tus lectores",
    words: "palabras",
    savedAt: "Guardado a las {time}",
    notes: "Notas",
    notesDescription: "Crea y fija notas a este artículo.",
    proofread: "Revisar",
    proofreadDescription: "Obtén ayuda para mejorar la claridad y el atractivo de tu artículo.",
    versions: "Versiones",
    settings: "Configuración",
    settingsDescription: "Configura tu publicación.",
    translations: "Traducciones",
    alsoIn: "También en",
    translationsDescription: "Traduce tu artículo automáticamente a otros idiomas. Llega a más lectores.",
    share: "Compartir",
    shareDescription: "Obtén un enlace para compartir tu artículo en redes sociales.",
    focusMode: "Modo concentración",
    focusModeDescription: "Convierte el editor en un modo sin distracciones para que puedas concentrarte en tu escritura.",
    translationOutdated: "Esta traducción se hizo para una versión anterior",
    mainLanguage: "Idioma principal",
    alternativeLanguages: "Idiomas alternativos",
    alternativeLanguagesDescription: "Activa las traducciones para tu post. Activa los idiomas para generarlos o desactívalos para eliminarlos.",
    translationsSaved: "Traducciones guardadas correctamente",
    translationOutdatedMessage: "Esta traducción está desactualizada.",
    translationUpToDate: "Esta traducción está actualizada.",
    regenerateTranslation: "Actualizarla",
    translationOutdatedOr: "O",
    markTranslationAsUpdated: "Marcar como actualizada",
    updatingTranslation: "Actualizando traducción...",
    translationRegenerated: "Traducción actualizada correctamente",
    saveChanges: "Guardar cambios",
    saveAndGenerate: "Guardar y generar",
    saveAndDelete: "Guardar y eliminar",
    publishPage: {
      update: "Actualizar",
      publish: "Publicar",
      delivery: "Entrega",
      deliveryDescription: "¿Cómo quieres entregar esta publicación?",
      sendViaEmail: "Enviar por correo",
      alreadySent: "Ya has enviado esta publicación a tus suscriptores por correo el {date}",
      subscribersWillReceive: "{{Ningún suscriptor|1 suscriptor|?? suscriptores}} recibirán esta publicación en su bandeja de entrada.",
      sendTestToMyself: "Enviarme prueba a mí mismo",
      testEmailSent: "Correo de prueba enviado correctamente",
      testEmailError: "Error al enviar el correo de prueba",
      audienceTitle: "Audiencia",
      audienceDescription: "Elige quién puede ver esta publicación.",
    },
  },
  editor: {
    defaultPlaceholder: "Escribe algo…",
    highlightColor: "Color de resaltado",
    noHighlight: "Sin resaltado",
    grey: "Gris",
    yellow: "Amarillo",
    blue: "Azul",
    green: "Verde",
    orange: "Naranja",
    red: "Rojo",
    bulletList: "Lista con viñetas",
    orderedList: "Lista numerada",
    image: "Imagen",
    code: "Código",
    quote: "Cita",
    subscribe: "Suscribirse",
    imageTitlePlaceholder: "Título de la imagen",
    imageDescriptionPlaceholder: "Descripción para accesibilidad",
    imageSizeSmall: "Pequeño",
    imageSizeMedium: "Mediano",
    imageSizeBig: "Ancho completo",
    turnIntoLabel: "Convertir en...",
    heading1: "Título 1",
    heading2: "Título 2",
    heading3: "Título 3",
    blockquote: "Cita",
    text: "Texto",
    callout: "Destacado",
    bold: "Negrita",
    italic: "Cursiva",
    strikethrough: "Tachado",
    inlineCode: "Código en línea",
    highlight: "Resaltado",
    link: "Enlace",
    horizontalRule: "Divisor",
    undo: "Deshacer",
    redo: "Rehacer",
    insertBlock: "Insertar bloque",
    more: "Más",
    snippet: "Snippet",
    codeBlock: "Bloque de código",
    math: "Math",
    embed: "Embed",
    embedDisplay: "Visualización",
    embedDisplayEmbed: "Interactivo",
    embedDisplayCard: "Tarjeta",
    language: "Idioma",
  },
  notes: {
    newNote: "Nueva nota",
    cancel: "cancelar",
    noPinnedYet: "aún no tienes ninguna nota",
    voice: "voz",
    copyToPost: "copiar al artículo",
  },
  blog: {
    title: "Publicación",
    audience: "Audiencia",
    forms: "Forms",
    stats: "Estadísticas",
    noResultsFound: "No se encontraron resultados",
    noPostsYet: "Aún no has creado ningún artículo.",
    filter: {
      spam: "Spam",
      archived: "Archivado",
      sent: "Enviado",
      published: "Publicado",
      draft: "Borrador",
    },
    blogName: "Nombre de la publicación",
    blogNameDescription: "Establece el nombre de tu publicación: se usará para identificar tu publicación y generar la URL",
    atLeast3Characters: "Usa de 3 a 15 caracteres: letras, números o guiones.",
    nameAvailable: "Este nombre está disponible.",
    nameInvalid: "Este nombre no es válido.",
    nameUnavailable: "Este nombre ya está en uso.",
    nameNaughty: "Este nombre no está permitido.",
    nameChangeCooldown: "Podrás cambiar tu nombre en {days} {{día|días}}.",
    nameChangeWarning: "Después de cambiar, no podrás cambiar tu nombre durante 30 días. Tu antigua dirección redirigirá a la nueva.",
    checkingNameAvailability: "Comprobando disponibilidad del nombre...",
    language: "Idioma",
    languageDescription: "Establece el idioma de tu publicación",
    additionalLanguages: "Idiomas adicionales",
    additionalLanguagesDescription: "Elige qué idiomas están disponibles para las traducciones en tu publicación. Todos los idiomas están activados por defecto.",
    titleAndDescription: "Título y descripción",
    titleAndDescriptionDescription: "Establece el título y la descripción de tu espacio",
    yourBlogTitle: "el-título-de-tu-publicación",
    description: "Descripción",
    yourBlogDescription: "la-descripción-de-tu-publicación",
    branding: "Marca",
    brandingDescription: "Añade tu propia marca a tu publicación y correos",
    logo: "Logotipo",
    logoDescription: "Se mostrará en la parte superior de tu publicación y en los correos. Tamaño recomendado: 200 x 200 píxeles.",
    uploadLogo: "Subir logotipo",
    wordmark: "Marca de texto",
    wordmarkDescription: "Se mostrará en lugar del nombre de tu publicación en la parte superior. Tamaño recomendado: 1000 x 200 píxeles.",
    uploadWordmark: "Subir marca de texto",
    subscribeImage: "Imagen de suscripción",
    subscribeImageDescription: "Se mostrará en el formulario de suscripción. Tamaño recomendado: 600 x 600 píxeles.",
    uploadSubscribeImage: "Subir imagen de suscripción",
    favicon: "Favicon",
    faviconDescription: "Se mostrará como el icono de la pestaña del navegador para tu publicación. Tamaño recomendado: 128 x 128 píxeles.",
    uploadFavicon: "Subir favicon",
    header: "Cabecera",
    headerDescription: "Añade una cabecera personalizada a cada uno de tus artículos",
    footer: "Pie",
    footerDescription: "Añade un pie personalizado a cada uno de tus artículos",
    welcomeEmail: {
      title: "Correo de bienvenida",
      description: "Personaliza el correo de bienvenida para nuevos suscriptores",
      subjectLabel: "Asunto del correo",
      subjectContent: "Contenido del correo",
    },
    export: {
      title: "Exportar",
      description: "Exporta todos los suscriptores y el contenido de tu publicación",
      generateExport: "Generar exportación",
      generating: "Generando...",
      exportedAt: "Exportado el",
      subscribers: "Suscriptores",
      posts: "Publicaciones",
      download: "Descargar",
      processing: "Procesando...",
      error: "Error",
      noExports: "Aún no hay exportaciones",
    },
    subscribersPage: {
      email: "Email",
      subscribedAt: "Suscrito el",
      tier: "Nivel",
      form: "Form",
      allForms: "All forms",
      direct: "Direct",
      free: "Gratis",
      allTiers: "Todos los niveles",
      noSubscribersFound: "No se encontraron suscriptores",
      noSubscribersYet: "Aún no hay suscriptores",
      publication: "Publicación",
      followBack: "Suscribirse",
      following: "Suscrito",
    },
    formsPage: {
      editorTitle: "Create form",
      editEditorTitle: "Edit form",
      createForm: "Create form",
      editForm: "Edit form",
      name: "Name",
      audience: "Destinatarios",
      audienceDescription: "Elige quién puede recibir el correo con los archivos. Si restringes el acceso, tendrán que suscribirse o subir de nivel primero.",
      downloads: "Files",
      subscribers: "Subscribers",
      size: "Size",
      actions: "Actions",
      everyone: "Todos",
      everyoneDescription: "Cualquiera puede enviar el formulario y recibir el correo con los archivos.",
      subscribersOnly: "Solo suscriptores",
      subscribersOnlyDescription: "Solo los suscriptores pueden recibir el correo con los archivos.",
      content: "Description",
      email: "Email",
      emailSubject: "Email subject",
      emailBody: "Email body",
      formSectionTitle: "Create form",
      formSectionDescription: "Set up your form and file delivery",
      filesSectionTitle: "Files",
      filesSectionDescription: "Upload the files readers will receive",
      emailSectionDescription: "Customize the delivery email sent after submit",
      defaultDescription: "Suscribete para recibir estos archivos por correo.",
      defaultEmailSubject: "Tus archivos estan listos",
      defaultEmailSubjectWithName: "{publicationName}: tus archivos estan listos",
      defaultEmailGreeting: "Hola,",
      defaultEmailIntro: "gracias por suscribirte. Tus enlaces de descarga estan abajo.",
      defaultEmailOutro: "Si tienes cualquier duda, responde a este correo.",
      defaultEmailSignature: "- El equipo",
      defaultEmailSignatureWithName: "- {publicationName}",
      dropFiles: "Drop the files",
      formCreated: "Form created",
      formUpdated: "Form updated",
      formDeleted: "Form deleted",
      blankSlate: {
        title: "Capture, nurture, convert",
        growReadership: "Grow your readership",
        growReadershipDescription: "Capture more readers by offering exclusive content behind a subscription form.",
        convertReaders: "Convert readers into paid subscribers",
        convertReadersDescription: "Offer useful downloads and resources that turn readers into subscribers.",
        goBeyondText: "Go beyond text",
        goBeyondTextDescription: "Give your audience more by supplementing your written content with downloadable media and resources.",
      },
    },
    reactionEmojis: "Emojis de reacción",
    reactionEmojisDescription: "Elige qué emojis pueden usar los lectores para reaccionar a tus publicaciones",
    addEmoji: "Añadir emoji",
    interval: "intervalo",
    referrer: "referente",
    visitors: "visitantes",
    views: "vistas",
    country: "país",
    channel: "canal",
  },
  categories: {
    culture: "Cultura",
    technology: "Tecnología",
    business: "Negocios",
    usPolitics: "Política EEUU",
    finance: "Finanzas",
    food: "Comida",
    podcast: "Podcast",
    sports: "Deportes",
    art: "Arte",
    worldPolitics: "Política Mundial",
    healthPolitics: "Política Sanitaria",
    news: "Noticias",
    fashionBeauty: "Moda y Belleza",
    music: "Música",
    faith: "Fe",
    climate: "Clima",
    science: "Ciencia",
    literature: "Literatura",
    fiction: "Ficción",
    health: "Salud",
    design: "Diseño",
    travel: "Viajes",
    parenting: "Crianza",
    philosophy: "Filosofía",
    comics: "Cómics",
    international: "Internacional",
    crypto: "Cripto",
    history: "Historia",
    humor: "Humor",
    education: "Educación",
    filmTv: "Cine y TV",
  },
  readerOnboarding: {
    subscribedTo: "Te has suscrito a \"{publicationTitle}\"",
    selectInterests: "¿Qué temas te interesan?",
    selectInterestsDescription: "Selecciona las categorías sobre las que te gustaría leer. Esto nos ayuda a recomendarte publicaciones que te encantarán.",
    continue: "Continuar",
    skip: "Saltar por ahora",
  },
  onboarding: {
    buildAudience: "Construye tu audiencia con tus propias reglas",
    ownYourContent: "En fika eres dueño de lo que escribes y de la comunidad que se reúne a su alrededor.",
    nameYourBlog: "Comienza nombrando tu publicación; tu propio rincón en la web abierta y salvaje.",
    yourBlogNamePlaceholder: "el-nombre-de-tu-publicación",
    createBlog: "Crear publicación",
    continue: "Continuar",
    updateBlog: "Actualizar publicación",
    onboardingRabbitAlt: "Conejo de inicio",
    chooseLanguage: {
      title: "¿En qué idiomas escribirás?",
      description: "Elige el idioma de tu publicación y habilita idiomas adicionales para las traducciones.",
    },
    createFirstPost: {
      title: "Publica tu primer artículo",
      description: "Comienza a escribir tu primer artículo y ponte en marcha con Fika. ¡Comparte tus pensamientos con el mundo!",
      descriptionWithDraft: "Continúa escribiendo tu primer artículo y ponte en marcha con Fika. ¡Comparte tus pensamientos con el mundo!",
      ignore: "Ignorar",
      createPost: "Crear artículo",
      continueWriting: "Continuar escribiendo",
    },
    createSecondPost: {
      title: "Publica tu segundo artículo",
      description: "Escribe otro artículo y compártelo con tus lectores.",
      descriptionWithDraft: "Continúa escribiendo tu segundo artículo.",
      ignore: "Ignorar",
      createPost: "Crear artículo",
      continueWriting: "Continuar escribiendo",
      creating: "Creando...",
    },
    fillProfile: {
      title: "Completa tu perfil",
      description: "Añade tu nombre, biografía e imagen de perfil para personalizar tu presencia en Fika.",
      ignore: "Ignorar",
      editProfile: "Editar perfil",
    },
    setLanguage: {
      title: "Establece el idioma de tu publicación",
      description: "Elige el idioma principal de tu publicación. Esto ayuda a los lectores a encontrar tu contenido.",
      ignore: "Ignorar",
      chooseLanguage: "Elegir idioma",
    },
    fillPublication: {
      title: "Añade detalles de la publicación",
      description: "Dale un título, descripción y logotipo a tu publicación para hacerla destacar.",
      ignore: "Ignorar",
      editDetails: "Editar detalles",
    },
    translateArticles: {
      title: "Traduce \"{title}\"",
      description: "Añade traducciones a tus artículos para llegar a lectores en diferentes idiomas.",
      ignore: "Ignorar",
      translateLatestArticle: "Traducir artículo",
    },
  },
  postSettings: {
    title: "configuración",
    customPagesTitle: "páginas personalizadas",
    customPagesDescription: "haz que este post aparezca en la barra de navegación",
    customPageLabel: "página personalizada",
    seoMetadataTitle: "metadatos seo",
    seoMetadataDescription: "el título y la descripción del post como aparecerán en los resultados de búsqueda",
    seoMetadataSaved: "metadatos guardados",
    metaTitleLabel: "metatítulo",
    metaTitlePlaceholder: "título de tu publicación",
    metaDescriptionLabel: "metadescripción",
    metaDescriptionPlaceholder: "descripción de tu publicación",
    canonicalTitle: "canónica",
    canonicalDescription: "si la fuente original del post es otro sitio web, puedes especificar la URL canónica aquí para evitar problemas de contenido duplicado.",
    canonicalUrlLabel: "url canónica",
    canonicalUrlSaved: "url canónica guardada",
    pinSaved: "página personalizada guardada",
    tagsTitle: "etiquetas",
    tagsDescription: "añade etiquetas a tu post",
    audienceSaved: "configuración de audiencia guardada",
    audienceTitle: "Audiencia",
    audienceDescription: "Elige quién puede acceder a esta publicación.",
    audienceEveryoneLabel: "Todos",
    audienceEveryoneDescription: "Cualquiera puede verlo, incluidos los visitantes que no están suscritos.",
    audienceAllSubscribersLabel: "Solo suscriptores",
    audienceAllSubscribersDescription: "Cualquier suscriptor puede ver esta publicación.",
    commentsEnabledTitle: "Comments",
    commentsEnabledDescription: "Allow readers to comment on this post.",
    publishedAtTitle: "fecha de publicación",
    publishedAtDescription: "cambiar la fecha en que este post fue publicado por primera vez.",
    publishedAtLabel: "fecha de publicación",
    publishedAtSaved: "fecha de publicación guardada",
    archivePostTitle: "archivar post",
    unarchivePostTitle: "desarchivar post",
    archivePostDescription: "oculta este post de tu publicación.",
    unarchivePostDescription: "muestra este post en tu publicación.",
    archivePostSaved: "post archivado",
    deletePostTitle: "eliminar post",
    deletePostDescription: "elimina este post por completo.",
    deletePostWarning: "esta acción no se puede deshacer.",
    deletePostSaved: "post eliminado",
    holdToDelete: "Mantén para eliminar",
    deleting: "Eliminando...",
    deleted: "¡Eliminado!",
  },
  postShare: {
    preview: "Vista previa",
    previewDescription: "Cómo aparecerá tu publicación en las redes sociales y los resultados de búsqueda",
    articleLink: "Enlace del artículo",
    copyLink: "Copiar enlace",
    linkCopied: "Enlace copiado al portapapeles",
    metaTitleLabel: "Meta título",
    metaTitlePlaceholder: "El título de tu publicación",
    metaDescriptionLabel: "Meta descripción",
    metaDescriptionPlaceholder: "La descripción de tu publicación",
    shareOnSocialMedia: "Compartir en redes sociales",
    shareOn: "Compartir en {network}",
    publishedCongrats: "¡Tu publicación está en línea!",
    shareNow: "¡Ahora compártela con el mundo! 🚀",
    backToPost: "Volver a la publicación",
    setCoverImage: "Establecer como portada",
    resetCoverImage: "Restablecer a la original",
    coverImageUpdated: "Imagen de portada actualizada",
    socialImages: "Imágenes para redes sociales",
    socialImagesDescription: "Descarga imágenes para redes sociales",
    download: "Descargar",
  },
  postVersions: {
    title: "versiones",
    loading: "cargando versiones...",
    failed: "error al cargar versiones",
    failedVersion: "error al cargar versión",
    restored: "versión restaurada correctamente",
    restore: "restaurar",
    language: "idioma",
    pills: {
      current: "actual",
      archived: "archivada",
      sent: "enviada",
      published: "publicada",
    },
  },
  subscribe: {
    defaultText: "Suscríbete a {userName} para recibir actualizaciones directamente en tu correo",
    button: "Suscribirse",
    emailPlaceholder: "Introduce tu correo",
  },
  snippet: {
    placeholder: "Describe lo que quieres crear...",
    placeholderUpdate: "Describe lo que quieres cambiar...",
    buttonCreate: "crear",
    buttonUpdate: "actualizar",
    buttonCreating: "Creando...",
    buttonUpdating: "Actualizando...",
  },
  auth: {
    signInOrSignUp: "Iniciar sesión",
    registrationsClosed: "Los registros están cerrados. Los usuarios existentes pueden iniciar sesión.",
    accountSuspendedTitle: "Cuenta suspendida",
    accountSuspendedMessage: "Tu cuenta ha sido suspendida. Escríbenos a humans@fika.bar si crees que es un error.",
    continueWithGoogle: "Continuar con Google",
    continueWithLinkedIn: "Continuar con LinkedIn",
    signingIn: "Iniciando sesión...",
    or: "o",
    enterYourEmail: "Introduce tu correo",
    failedToSend: "Error al enviar el código de verificación. Inténtalo de nuevo.",
    sending: "Enviando...",
    enterCode: "Introduce el código de 6 dígitos que se ha enviado a",
    verifying: "Verificando...",
    invalidCode: "Código inválido o caducado. Inténtalo de nuevo.",
    useDifferentEmail: "Usar un correo diferente",
    subscribeTo: "Suscribirse a",
    thisBlog: "esta publicación",
    getUpdates: "Suscríbete para recibir actualizaciones en tu bandeja de entrada",
    subscribe: "Suscribirse",
    somethingWentWrong: "Algo salió mal",
    disclaimerPrefix: "Al enviar el formulario, aceptas la",
    disclaimerPrivacy: "política de privacidad",
    disclaimerAnd: "y aceptas los",
    disclaimerTerms: "términos del servicio",
    tierSelectionTitleSubscribed: "¡Estás suscrito a {publicationName}!",
    tierSelectionTitleSubscribedNoName: "¡Estás suscrito!",
    subscriptionError: "Se produjo un error. Por favor, inténtalo de nuevo.",
    verifyEmailSuccess: "¡Correo electrónico verificado correctamente!",
    emailNotVerified: "Por favor, verifica tu correo electrónico para realizar esta acción",
    subscriptionConfirmationDescriptionBeforeAuthor: "Recibirás actualizaciones de ",
    subscriptionConfirmationDescriptionAfterAuthor: " directamente en tu bandeja de entrada.",
    subscriptionConfirmationDescriptionNoAuthor: "Recibirás actualizaciones directamente en tu bandeja de entrada.",
  },
  unsubscribe: {
    writerSuccess: "te has dado de baja",
    writerSuccessDescription: "ya no recibirás correos de este escritor. siempre puedes volver a suscribirte desde su perfil.",
    marketingSuccess: "te has dado de baja",
    marketingSuccessDescription: "ya no recibirás correos promocionales de fika. puedes gestionar tus preferencias en ajustes.",
    weeklyFeaturedSuccess: "te has dado de baja",
    weeklyFeaturedSuccessDescription: "ya no recibirás el correo semanal de artículos destacados. puedes volver a activarlo en ajustes.",
    error: "algo salió mal",
    invalidToken: "este enlace de cancelación de suscripción no es válido o ha caducado. inténtalo de nuevo desde un correo reciente.",
    missingToken: "no se proporcionó ningún token de cancelación de suscripción. usa el enlace de tu correo.",
    genericError: "ocurrió un error inesperado. inténtalo de nuevo más tarde.",
    invalidRequest: "solicitud no válida",
    invalidRequestDescription: "esta página requiere un enlace de cancelación de suscripción válido de un correo.",
    signInToManage: "iniciar sesión para gestionar suscripciones",
  },
  subscriptions: {
    title: "Suscripciones",
    manage: "Gestionar",
    manageTitle: "Gestionar suscripciones",
    viewOriginal: "Ver original",
    noPublishedPostsYet: "Aún no hay publicaciones publicadas",
    previousPost: "Artículo anterior",
    nextPost: "Artículo siguiente",
    following: "Siguiendo",
    subscribed: "Suscrito",
    renewsAt: "Se renueva el {date}",
    upgradePlan: "Mejorar plan",
    tierChangeError: "Error al cambiar el plan",
    manageBilling: "Facturación y recibos",
    subscribersOnly: "Solo suscriptores",
    tableOfContents: "Tabla de contenidos",
  },
  discover: {
    readMore: "Leer más",
    upgradeToRead: "Mejora tu plan para leer",
    humanContent: "Humano",
    humanContentTitle: "Contenido humano",
    humanContentDescription: "Fika tiene una alta confianza en que este contenido fue escrito por un humano con poca o ninguna ayuda de la IA.",
  },
  privatePost: {
    subscribeCta: "Suscribirse",
    upgradeCta: "Actualizar para desbloquear",
  },
  applicationLayout: {
    loadingPhrases: {
      synchronizing: "sincronizando...",
      hadronCollider: "montando el colisionador de hadrones...",
      reticulatingSplines: "reticulando splines...",
      wakingHamsters: "despertando a los hámsters...",
      consultingOracle: "consultando el oráculo...",
      aligningStars: "alineando las estrellas...",
      fluxCapacitor: "calibrando el condensador de flujo...",
      traveling88mph: "viajando a 88 mph...",
      whiteRabbit: "siguiendo al conejo blanco...",
      reversingPolarity: "invirtiendo la polaridad...",
      enhancingImage: "mejorando la imagen...",
      enteringMatrix: "entrando en la matriz...",
      findingRing: "encontrando el anillo único...",
      dividingByZero: "dividiendo por cero...",
    },
  },
  proofreader: {
    title: "Corrector",
    editorTitle: "Editor",
    editorDescription: "Revisa tu artículo para mejorar la claridad y el impacto.",
    principles: {
      grammar: {
        label: "Gramática",
        description: "Corrige errores ortográficos y gramaticales.",
        action: "Corregir gramática",
      },
      tighten: {
        label: "Condensar",
        description: "Haz el texto más conciso.",
        action: "Condensar texto",
      },
      refine: {
        label: "Refinar",
        description: "Mejora la claridad, el flujo y el impacto del texto.",
        action: "Refinar texto",
      },
    },
    noFixesFound: "No se encontraron correcciones",
    countFixes: "{{Ninguna corrección|1 corrección|?? correcciones}}",
    countFixesFound: "{{Ninguna corrección encontrada|1 corrección encontrada|?? correcciones encontradas}}",
    scanning: "Escaneando...",
    percentComplete: "{percent}% completado",
    discard: "Descartar",
    acceptAll: "Aceptar todo",
    lenses: {
      title: "Lentes",
      description: "Visualiza la estructura de las frases para mejorar el ritmo y la legibilidad.",
      off: "apagado",
      difficulty: "dificultad",
      difficultyDescription: "Muestra qué tan difíciles son tus frases. Las frases más difíciles aparecen en colores más cálidos. Para mejorar, usa frases más cortas y palabras más simples.",
      variation: "variación",
      variationDescription: "Muestra qué tan variadas son las longitudes de tus frases. Las frases monótonas aparecen en colores más cálidos. Para mejorar, usa una mezcla de frases cortas y largas.",
      legend: {
        difficulty: {
          orange: "Muy difícil de leer",
          amber: "Difícil de leer",
        },
        variation: {
          orange: "Muy monótona",
          amber: "Monótona",
        },
      },
      tips: {
        veryHard: "Esta frase es muy difícil de leer. Intenta dividirla en frases más cortas o usar palabras más simples.",
        hard: "Esta frase es difícil de leer. Considera simplificarla.",
        veryMonotonous: "Tus frases son muy similares en longitud. Varía el ritmo mezclando frases cortas y largas.",
        monotonous: "Esta frase es similar a las cercanas. Intenta variar la longitud para mejorar el flujo.",
      },
    },
  },
  inbox: {
    title: "Bandeja de entrada",
    message: "Mensaje",
    empty: "No hay cartas ni comentarios todavía",
    emptyLetters: "No hay cartas todavía",
    emptyComments: "No hay comentarios todavía",
    emptyDescription: "Usa este espacio para ver cartas de tus lectores y conversaciones con los autores a los que estás suscrito.",
    unread: "Sin leer",
    read: "Leídos",
    inReplyTo: "En respuesta a:",
    writeMessage: "Escribe un mensaje...",
    submit: "Enviar",
    publishing: "Publicando...",
    you: "Tú",
    tabAll: "Todo",
    tabEmails: "Cartas",
    tabComments: "Comentarios",
    typePrivateEmail: "Carta privada",
    typePublicComment: "Comentario público",
    andMore: "y {count} más",
  },
  comments: {
    title: "Comentarios",
    writeComment: "Escribe un comentario...",
    submit: "Publicar comentario",
    publishing: "Publicando...",
    reply: "Responder",
    deleting: "Eliminando...",
    delete: "Eliminar",
    deleteConfirm: "¿Eliminar este comentario?",
    empty: "Aún no hay comentarios. ¡Sé el primero en comentar!",
    anonymousParticipant: "Un suscriptor",
    flaggedMessage: "Tu mensaje fue marcado como spam o contenido inapropiado y no pudo ser enviado.",
    author: "Autor",
    privateVisibility: "Solo visible para ti y los participantes",
    sendAsPrivate: "Enviar como carta privada",
    translateTo: "Traducir a {locale}",
    showOriginal: "Mostrar original ({locale})",
  },
} satisfies Translation

export default es
